import { MAX_SELECTABLE_GENRE_TAGS } from '@arpeggi/shared'
import { Box } from '@arpeggi/ui'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { observer } from 'mobx-react-lite'
import { ComponentProps, default as React, FC, SVGProps, useCallback, useContext, useEffect, useRef } from 'react'
import ReactCanvasConfetti from 'react-canvas-confetti'
import { useNavigate } from 'react-router-dom'
import { GENRES } from '../../../../client/Genres'
import { useAuth } from '../../../../hooks/useAuth'
import { css, styled } from '../../../../lib/stitches'
import { IStore, useTreeState } from '../../../../stores/Root'
import { Row, Spacer, Text } from '../../../core/Components'
import CopyTextButton from '../../../core/CopyTextButton'
import { HoneyPotFields, IHoneyPotFields } from '../../../core/HoneyPotFields'
import { TwitterLogo } from '../../../core/icons'
import { ARPEGGI_TWITTER_NICKNAME, TWEET_BASE_URL } from '../../../detail-pages/shared/TweetButton'
import { gray15, gray3, gray5, gray8 } from '../../../platform/color'
import { PreviewSongPlayer } from '../../../player/Player'
import { useSongTitle } from '../../../studio/StudioHeader'
import Multiselect from '../Multiselect'
import Progress from '../Progress'
import { UploadModalContext } from '../upload-modal'
import { Button } from '../upload-modal/UploadModalFooter'
import { Title } from '../upload-modal/UploadModalNavigation'
import ColorPicker from './ColorPicker'
import useMintModalSteps from './useMintModalSteps'

const Subtitle = styled('h5', {
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  marginBottom: '16px'
})

const FieldLabel = styled('label', {
  fontSize: '12px',
  lineHeight: '20px',
  paddingBottom: '6px',
  fontWeight: 700,
  color: 'black'
})

const TextField = styled('input', {
  border: '1px solid ' + gray5,
  borderRadius: '6px',
  background: 'transparent',
  padding: '10px 12px',
  fontSize: '14px',
  lineHeight: '20px',
  outline: 'none',
  color: gray15,
  transition: 'all 100ms ease',
  marginBottom: '12px',
  '&:placeholder': {
    color: gray8
  },
  '&:focus': {
    boxShadow: '0px 0px 0px 3px ' + gray3
  }
})

type GraphicsProps = SVGProps<SVGElement>
const Graphics: FC<GraphicsProps> = (props) => (
  /* @ts-ignore */
  <svg
    width="90"
    height="45"
    viewBox="0 0 90 45"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <rect x="90" y="45" width="45" height="15" transform="rotate(-180 90 45)" fill="#E7B536" />
    <rect x="45" y="30" width="45" height="15" transform="rotate(-180 45 30)" fill="#F43E72" />
    <rect x="90" y="15" width="45" height="15" transform="rotate(-180 90 15)" fill="#4791FF" />
  </svg>
)

const topGraphics = css({
  position: 'absolute',
  top: '32px',
  right: '0px'
})

const StyledPreviewContainer = styled('div', {
  boxShadow: '0px 0px 13.8988px -1.98554px rgba(0, 0, 0, 0.3)',
  borderRadius: '12px',
  overflow: 'hidden',
  height: '373px',
  width: '373px'
})

interface MintModalDetailsProps {
  genres: Set<string>
  handleGenreSelect: (genre: string) => void
  handleGenreRemove: (genre: string) => void
  handleColorPick: (newColor: string) => void
  isLoading?: boolean
  currentColor: string
  handleHoneyPotChange: (honeyPotFields: IHoneyPotFields) => void
  honeyPotFields: IHoneyPotFields
}

const MintModalDetails: React.FC<MintModalDetailsProps> = observer(
  ({
    genres,
    handleGenreSelect,
    handleGenreRemove,
    isLoading,
    handleColorPick,
    currentColor,
    honeyPotFields,
    handleHoneyPotChange
  }) => {
    const { stepForward, customArtistAddress, setCustomArtistAddress } =
      useContext(UploadModalContext)
    const { name, handleChange } = useSongTitle()
    const flags = useFlags()
    const { user } = useAuth()

    return (
      <Box css={{ display: 'flex' }}>
        <HoneyPotFields
          honeyPotFields={honeyPotFields}
          handleHoneyPotChange={handleHoneyPotChange}
        />

        <Box
          css={{ display: 'flex', flexDirection: 'column', width: '350px', marginRight: '64px' }}
        >
          <Title>Mint your song</Title>
          <Subtitle>
            Publish your song and receive an NFT for free!
            <br />
            It will be available for everyone to remix and listen to.
          </Subtitle>
          <FieldLabel htmlFor="title">Track title*</FieldLabel>
          <TextField
            id="title"
            placeholder="Your track title"
            disabled={isLoading}
            value={name}
            onChange={handleChange}
          />
          {flags.enableCustomArtistAddress && user?.type === 'admin' && (
            <TextField
              id="artist"
              placeholder="Custom Artist Address"
              disabled={isLoading}
              value={customArtistAddress}
              onChange={(value) => setCustomArtistAddress(value.currentTarget.value)}
            />
          )}
          <FieldLabel>
            Genre* <span style={{ color: gray8 }}>(up to {MAX_SELECTABLE_GENRE_TAGS})</span>
          </FieldLabel>
          <Multiselect
            values={genres}
            options={GENRES}
            onSelect={handleGenreSelect}
            onRemove={handleGenreRemove}
          />
          <FieldLabel css={{ marginTop: '12px' }}>Select your token color*</FieldLabel>
          <ColorPicker
            onPick={handleColorPick}
            currentColor={currentColor}
            css={{ marginBottom: '16px' }}
          />
          <Spacer />
          <Button
            onClick={() => {
              stepForward()
            }}
            disabled={genres.size === 0 || !name}
          >
            Mint song
          </Button>
        </Box>
        <StyledPreviewContainer>
          <PreviewSongPlayer />
        </StyledPreviewContainer>
      </Box>
    )
  }
)

export const MintModalLoader: React.FC<ComponentProps<typeof Box>> = ({ css, ...rest }) => (
  <Box css={{ padding: '10px', width: '450px', ...css }} {...rest}>
    <Graphics className={topGraphics()} />
    <Title>Minting in progress</Title>
    <Subtitle>Please wait...</Subtitle>
    <Progress infinite />
  </Box>
)

const StyledCanvas = styled(ReactCanvasConfetti, {
  position: 'fixed',
  pointerEvents: 'none',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0
})

const useConfetti = () => {
  const refAnimationInstance = useRef(null)

  const getInstance = useCallback((instance) => {
    refAnimationInstance.current = instance
  }, [])

  const makeShot = useCallback((particleRatio, opts) => {
    refAnimationInstance.current &&
      refAnimationInstance.current({
        ...opts,
        origin: { y: 0.7 },
        particleCount: Math.floor(200 * particleRatio),
        colors: ['#E7B536', '#F43E72', '#4791FF']
      })
  }, [])

  const fireConfetti = useCallback(() => {
    makeShot(0.25, {
      spread: 26,
      startVelocity: 55
    })

    makeShot(0.2, {
      spread: 60
    })

    makeShot(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8
    })

    makeShot(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2
    })

    makeShot(0.1, {
      spread: 120,
      startVelocity: 45
    })
  }, [makeShot])

  return { fireConfetti, getInstance }
}

const TWEET_TEXT = `Just minted a new song on @${ARPEGGI_TWITTER_NICKNAME}`

export const MintModalSuccess: React.FC<{ detailPagePath: string }> = ({ detailPagePath }) => {
  const navigate = useNavigate()
  const { getInstance, fireConfetti } = useConfetti()

  useEffect(() => fireConfetti(), [])

  const link = window.location.origin + detailPagePath
  const twitterLink = `${TWEET_BASE_URL}${TWEET_TEXT} ${link}`

  const handleTweet = () => window.open(encodeURI(twitterLink), '_blank')

  return (
    <>
      <Box css={{ padding: '10px', width: '450px' }}>
        <Graphics className={topGraphics()} />
        <Title>Congratulations!</Title>
        <Subtitle>
          Your song has been minted! View your song page by following the link below.
        </Subtitle>
        <Box css={{ marginBottom: '16px ' }}>
          <CopyTextButton link={link} />
        </Box>
        <Row>
          <Button onClick={() => navigate(detailPagePath)} css={{ flex: 1, marginRight: '4px' }}>
            Go to the song
          </Button>
          <Button
            secondary
            onClick={handleTweet}
            css={{ flex: 1, marginLeft: '4px', color: '$gray15' }}
          >
            <TwitterLogo style={{ marginRight: '6px' }} />
            Share on Twitter
          </Button>
        </Row>
      </Box>
      <StyledCanvas refConfetti={getInstance} />
    </>
  )
}

const StyledErrorContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'black'
})

const UnableToMint: React.FC<{ reason: string }> = ({ reason }) => (
  <StyledErrorContainer>
    <Text css={{ fontWeight: 700, marginBottom: '12px' }}>Unable to mint your song.</Text>
    <Text css={{ marginBottom: 0 }}>{reason}</Text>
  </StyledErrorContainer>
)

const MintModalSteps = observer(() => {
  const { studio } = useTreeState() as IStore
  const { stepIndex, stepForward, customArtistAddress } = useContext(UploadModalContext)

  const {
    genres,
    handleGenreSelect,
    handleGenreRemove,
    detailPagePath,
    unableToMintReason,
    honeyPotFields,
    setHoneyPotFields
  } = useMintModalSteps({
    stepIndex,
    stepForward,
    studio,
    customArtistAddress
  })

  if (unableToMintReason) {
    return <UnableToMint reason={unableToMintReason} />
  }

  switch (stepIndex) {
    case 0: {
      return (
        <MintModalDetails
          genres={genres}
          handleGenreSelect={handleGenreSelect}
          handleGenreRemove={handleGenreRemove}
          handleColorPick={studio.song.setTokenColor}
          currentColor={studio.song.tokenColor}
          honeyPotFields={honeyPotFields}
          handleHoneyPotChange={(honeyPotFields) => setHoneyPotFields(honeyPotFields)}
        />
      )
    }
    case 1: {
      return <MintModalLoader />
    }
    case 2: {
      return <MintModalSuccess detailPagePath={detailPagePath} />
    }
  }
})

export default MintModalSteps
